import axiosInstance from "../lib/axios.instance";
import { getAccessToken } from "../utils/storage.utils";
import {
  ICompasAIQueryRequest,
  ICompasAIActionsRequest,
  IActionRunResponse,
  ICompasAIChatHistoryResponse,
  INextActionResponse,
  IActionOutputResponse,
  IReorderActionsRequest,
  IReorderActionsResponse,
  IEditActionPromptRequest,
  IAnonymizePromptRequest,
  IAnonymizePromptResponse,
  IChatMessagesChainRequest,
  IChatMessagesChainResponse,
} from "../types/compass.types";

const BASE_URL = "compas-ai";

/**
 * Query compass AI with streaming response
 * Note: For streaming, we use fetch API instead of axios
 */
export const compasAIQueryStream = async (
  payload: ICompasAIQueryRequest
): Promise<ReadableStream<Uint8Array>> => {
  try {
    // Get the base URL from axios instance defaults
    const apiUrl = axiosInstance.defaults.baseURL || "https://api-dev.legalassistant.au";

    // Get token from Office.js settings (same way axios does)
    const token = await getAccessToken();

    if (!token) {
      throw new Error("No access token available");
    }

    const response = await fetch(`${apiUrl}${BASE_URL}/converse/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    return response.body;
  } catch (error: any) {
    throw new Error(error.message || "Failed to query compass AI");
  }
};

/**
 * Get compass AI chat history
 */
export const getCompassChatHistory = async (
  documentId: string
): Promise<ICompasAIChatHistoryResponse> => {
  try {
    const response = await axiosInstance.get<ICompasAIChatHistoryResponse>(
      `${BASE_URL}/history?context_id=${documentId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to fetch compass chat history"
    );
  }
};

/**
 * Run compass AI actions
 */
export const runCompassActions = async (
  payload: ICompasAIActionsRequest | ICompasAIActionsRequest[]
): Promise<IActionRunResponse> => {
  try {
    const response = await axiosInstance.post<IActionRunResponse>(
      `${BASE_URL}/actions/run`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to run compass actions"
    );
  }
};

/**
 * Get next action from queue
 */
export const getNextAction = async (documentId: string): Promise<INextActionResponse> => {
  try {
    const response = await axiosInstance.get<INextActionResponse>(
      `${BASE_URL}/queue/next?context_id=${documentId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || "Failed to get next action");
  }
};

/**
 * Get all queued actions
 */
export const getAllQueuedActions = async (documentId: string): Promise<any> => {
  try {
    const response = await axiosInstance.get<any>(`${BASE_URL}/actions?context_id=${documentId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to get queued actions"
    );
  }
};

/**
 * Remove queued action by ID
 */
export const cancelQueuedActionById = async (
  documentId: string,
  jobIds: string[]
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (documentId && jobIds.length > 0) {
      queryParams.append("context_id", documentId);
      queryParams.append("item_ids", jobIds.join(","));
    }
    const response = await axiosInstance.post<any>(
      `${BASE_URL}/queue/cancel?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to remove queued action"
    );
  }
};

/**
 * Get action status
 */
export const getActionsStatus = async (itemIds: string[]): Promise<any> => {
  try {
    const response = await axiosInstance.post<any>(`${BASE_URL}/queue/status`, itemIds);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to get actions status"
    );
  }
};

/**
 * Rerun failed action
 */
export const rerunFailedAction = async (itemId: string): Promise<any> => {
  try {
    const response = await axiosInstance.post<any>(`${BASE_URL}/actions/rerun/${itemId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || "Failed to rerun action");
  }
};

/**
 * Get action output
 */
export const getActionOutput = async (itemId: string): Promise<IActionOutputResponse> => {
  try {
    const response = await axiosInstance.get<IActionOutputResponse>(
      `${BASE_URL}/actions/output/${itemId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to get action output"
    );
  }
};

/**
 * Reorder actions
 */
export const reorderActions = async (
  payload: IReorderActionsRequest
): Promise<IReorderActionsResponse> => {
  try {
    const { context_id, ...requestBody } = payload;
    const response = await axiosInstance.post<IReorderActionsResponse>(
      `${BASE_URL}/history/reorder-actions?context_id=${context_id}`,
      requestBody
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || "Failed to reorder actions");
  }
};

/**
 * Edit action prompt
 */
export const editActionPrompt = async (
  actionId: string,
  contextId: string,
  prompt: string
): Promise<any> => {
  try {
    const payload: IEditActionPromptRequest = { prompt };
    const response = await axiosInstance.post<any>(
      `${BASE_URL}/actions/edit-prompt/${actionId}?context_id=${contextId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to edit action prompt"
    );
  }
};

/**
 * Delete action
 */
/**
 * Delete action
 */
export const deleteAction = async (actionId: string, contextId: string): Promise<any> => {
  try {
    const response = await axiosInstance.delete<any>(
      `${BASE_URL}/actions/${actionId}?context_id=${contextId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || "Failed to delete action");
  }
};

/**
 * Anonymize prompt
 */
export const anonymizePrompt = async (
  payload: IAnonymizePromptRequest
): Promise<IAnonymizePromptResponse> => {
  try {
    const response = await axiosInstance.post<IAnonymizePromptResponse>(
      `${BASE_URL}/anonymize-prompt`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to anonymize prompt"
    );
  }
};

/**
 * Chat messages chain
 */
export const chatMessagesChain = async (
  payload: IChatMessagesChainRequest
): Promise<IChatMessagesChainResponse> => {
  try {
    const response = await axiosInstance.post<IChatMessagesChainResponse>(
      `ai/chat/messages/chain`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || error.message || "Failed to chain messages"
    );
  }
};
